import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SecurityToolbar } from "./SecurityToolbar";
import { CreateAccountModal } from "./CreateAccount";
import AppContext from "../../context/AppContext";
import Cookies from "js-cookie";
import {
  searchProtectedAccounts,
  fetchProtectionColumnValues,
  fetchParticularAccount,
  deleteProtectedAccounts,
} from "../../service/Security";
import { Spinner } from "../../Spinner";
import translations from "../common/Translations";
import moment from "moment";
import { getPermissionByScreenCode } from "../../utils/permissions";
import PageSizeSelector from "../common/PageSizeSelector";
import useDirection from "../../hooks/useDirection";
import ModalCloseButton from "../common/ModalCloseButton";
import { Pencil, Trash2, Info, Filter, Search, SearchX, Inbox } from "lucide-react";
import ActionIconButton from "../common/ActionIconButton";
import PagerArrowButton from "../common/PagerArrowButton";
import DeletePermission from "../suspence/DeletePermission";
import { toast } from "../../utils/toast";
import { isRTL } from "../../utils/rtl";
import TableScrollShell from "../common/TableScrollShell";
import Tooltip from "../common/Tooltip";
import OverflowTooltipText from "../common/OverflowTooltipText";
import { BLANK_FILTER_LABEL, BLANK_FILTER_VALUE, TEXT_FILTER_DEBOUNCE_MS } from "../../utils/taskListFilters";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Only shows the shared blue tooltip when the text is actually clipped by
// its own `truncate`, not on every cell.
const TruncatedCellText = ({ text, className = "" }) => (
  <OverflowTooltipText text={text} className={className} />
);

const COLUMN_FILTER_POPOVER_WIDTH = 232;
const COLUMN_VALUES_LIMIT = 200;

const DATE_FILTER_POPOVER_WIDTH = 272;

// Table column id -> backend column name for /search and /column-values.
const FILTER_API_COLUMNS = {
  createdBy: "createdByName",
  accno: "accountNo",
  permanent: "permanent",
  updatedBy: "updatedByName",
  fromdate: "fromDate",
  protectionValid: "toDate",
  cretDate: "createdAt",
  updatedAt: "updatedAt",
  remark: "remarks",
  previousRemarks: "previousRemarks",
};

// Value-list columns hold a Set (op "in"); date and text columns hold { op: "between" | "contains", ... }.
const toSearchFilters = (filters) =>
  Object.entries(filters).map(([colId, filter]) =>
    filter instanceof Set
      ? { column: FILTER_API_COLUMNS[colId], op: "in", values: [...filter] }
      : { column: FILTER_API_COLUMNS[colId], ...filter }
  );

// /column-values takes { column, value } and only for value-list filters.
const toColumnConditions = (filters, exceptColId) =>
  Object.entries(filters)
    .filter(([colId, filter]) => colId !== exceptColId && filter instanceof Set)
    .map(([colId, values]) => ({ column: FILTER_API_COLUMNS[colId], value: [...values] }));

const isValidYmd = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
};

const parseLocalYmd = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatLocalYmd = (date) => {
  if (!date) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${mm}-${dd}`;
};

const formatRangeText = (from, to) => (from || to ? `${from || "\u2014"} to ${to || "\u2014"}` : "");

// Portal, fixed position and outside-click/Escape close shared by every column filter popup.
const FilterPopoverShell = ({ anchorEl, onClose, langMode, width = COLUMN_FILTER_POPOVER_WIDTH, children }) => {
  const [popoverStyle, setPopoverStyle] = useState({});
  const popoverRef = useRef(null);
  const isRtl = isRTL(langMode);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const padding = 8;
      const gap = 6;
      const popoverWidth = Math.min(width, window.innerWidth - padding * 2);
      const left = Math.min(
        Math.max(padding, rect.right - popoverWidth),
        window.innerWidth - popoverWidth - padding
      );
      // Open below; flip above when it doesn't fit, else pin it inside the viewport and scroll.
      const maxHeight = window.innerHeight - padding * 2;
      const height = Math.min(popoverRef.current?.scrollHeight || 0, maxHeight);
      let top = rect.bottom + gap;
      if (top + height > window.innerHeight - padding) {
        const aboveTop = rect.top - gap - height;
        top = aboveTop >= padding ? aboveTop : Math.max(padding, window.innerHeight - padding - height);
      }
      setPopoverStyle({ position: "fixed", top, left, width: popoverWidth, maxHeight, overflowY: "auto" });
    };
    updatePosition();
    // The content's height changes as values or the calendar load.
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePosition) : null;
    Array.from(popoverRef.current?.children || []).forEach((child) => resizeObserver?.observe(child));
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl, width]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorEl?.contains(e.target)) return;
      onClose();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose, anchorEl]);

  return createPortal(
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="z-[9999999] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
    >
      {children}
    </div>,
    document.body
  );
};

// Date range picker, same layout as TaskListView's date column filter; applied on Apply.
const DateRangeColumnFilter = ({ value, onChange, onClose, anchorEl, langMode }) => {
  const [from, setFrom] = useState(value?.from || "");
  const [to, setTo] = useState(value?.to || "");
  const [inputText, setInputText] = useState(formatRangeText(value?.from, value?.to));

  const setRange = (nextFrom, nextTo) => {
    setFrom(nextFrom);
    setTo(nextTo);
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    const [start = "", end = ""] = text.match(/\d{4}-\d{2}-\d{2}/g) || [];
    setRange(isValidYmd(start) ? start : "", isValidYmd(end) ? end : "");
  };

  const apply = () => {
    // A single picked day filters that day only.
    onChange(from || to ? { op: "between", from: from || to, to: to || from } : null);
    onClose();
  };

  return (
    <FilterPopoverShell anchorEl={anchorEl} onClose={onClose} langMode={langMode} width={DATE_FILTER_POPOVER_WIDTH}>
      <div className="w-full flex flex-col items-center">
        <input
          type="text"
          dir="ltr"
          value={inputText}
          onChange={handleInputChange}
          placeholder="YYYY-MM-DD to YYYY-MM-DD"
          className="w-full min-w-0 h-8 mb-2 text-xs border rounded-lg px-2.5 bg-slate-50 placeholder:text-slate-400 text-slate-700 font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
        <div className="react-datepicker-inline-container border border-slate-100 rounded-lg overflow-hidden shadow-sm">
          <DatePicker
            selected={null}
            startDate={parseLocalYmd(from)}
            endDate={parseLocalYmd(to)}
            onChange={(dates) => {
              const [start, end] = dates || [];
              const nextFrom = formatLocalYmd(start);
              const nextTo = formatLocalYmd(end);
              setRange(nextFrom, nextTo);
              setInputText(formatRangeText(nextFrom, nextTo));
            }}
            selectsRange
            inline
          />
        </div>
        <div className="w-full flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              onClose();
            }}
            className="text-[11px] font-medium text-slate-400 hover:text-slate-600 hover:underline"
          >
            {translations[langMode]?.clear ?? "Clear"}
          </button>
          <button
            type="button"
            onClick={apply}
            className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]"
          >
            {translations[langMode]?.apply ?? "Apply"}
          </button>
        </div>
      </div>
    </FilterPopoverShell>
  );
};

// Free-text "contains" filter, same as TaskListView's ColumnContainsFilter; applied on OK or Enter.
const ContainsColumnFilter = ({ value, onChange, onClose, anchorEl, langMode }) => {
  const [draft, setDraft] = useState(value?.value || "");

  const apply = () => {
    const text = draft.trim();
    onChange(text ? { op: "contains", value: text } : null);
    onClose();
  };

  return (
    <FilterPopoverShell anchorEl={anchorEl} onClose={onClose} langMode={langMode}>
      <div className="relative mb-2">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") apply();
          }}
          placeholder={`${translations[langMode]?.search ?? "Search"}...`}
          className="w-full h-8 pl-7 pr-2 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => {
            onChange(null);
            onClose();
          }}
          className="text-[11px] font-medium text-slate-400 hover:underline"
        >
          {translations[langMode]?.clear ?? "Clear"}
        </button>
        <button
          type="button"
          onClick={apply}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]"
        >
          OK
        </button>
      </div>
    </FilterPopoverShell>
  );
};

// Same portal/fixed-position/outside-click mechanics as ColumnFilterPopover
// in src/pages/users/UserList.js -- ported locally since no shared version
// exists in src/components/common/. Checkboxes edit a draft that is applied on
// OK, matching TaskListView; closing without OK discards it.
const ColumnFilterPopover = ({
  options,
  selected,
  onChange,
  onClose,
  anchorEl,
  langMode,
  loading,
  onSearchChange,
  getOptionLabel = (opt) => opt,
}) => {
  const [search, setSearch] = useState("");
  const searchActive = search.trim() !== "";

  // Options come from the server, so the search box asks it again after a pause.
  useEffect(() => {
    const timer = setTimeout(() => onSearchChange?.(search.trim()), TEXT_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // null until the options land, so an empty draft isn't read as "nothing checked".
  const [draft, setDraft] = useState(null);
  const optionsKey = options.join("");
  useEffect(() => {
    if (loading) return;
    // Search results start all ticked, like Excel; otherwise show the applied filter.
    setDraft(new Set(selected && !searchActive ? [...selected] : options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey, loading]);

  const optionText = (opt) => (opt === BLANK_FILTER_VALUE ? BLANK_FILTER_LABEL : String(getOptionLabel(opt)));
  const filteredOptions = options.filter((opt) =>
    optionText(opt).toLowerCase().includes(search.trim().toLowerCase())
  );

  const toggleOption = (opt) => {
    setDraft((prev) => {
      const next = new Set(prev || []);
      if (next.has(opt)) next.delete(opt);
      else next.add(opt);
      return next;
    });
  };

  const apply = () => {
    if (searchActive) {
      // While searching, OK keeps only the ticked matches.
      const picked = new Set(filteredOptions.filter((opt) => draft?.has(opt)));
      onChange(picked.size === 0 ? null : picked);
    } else {
      const picked = new Set(options.filter((opt) => draft?.has(opt)));
      // All-checked and none-checked both mean "don't filter this column".
      onChange(picked.size === 0 || picked.size === options.length ? null : picked);
    }
    onClose();
  };

  return (
    <FilterPopoverShell anchorEl={anchorEl} onClose={onClose} langMode={langMode}>
      <div className="relative mb-2">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translations[langMode].search}
          className="w-full h-8 pl-7 pr-2 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <div className="max-h-44 overflow-y-auto space-y-0.5">
        {loading && (
          <p className="text-xs text-slate-400 px-1 py-2">{`${(translations[langMode]?.Loading ?? "Loading").replace(/[.۔]+$/, "")}...`}</p>
        )}
        {!loading && filteredOptions.length === 0 && (
          <p className="text-xs text-slate-400 px-1 py-2">{translations[langMode].noDatavb}</p>
        )}
        {!loading && filteredOptions.map((opt) => (
          <label
            key={opt === BLANK_FILTER_VALUE ? "__blank__" : opt}
            className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 cursor-pointer text-xs text-slate-700"
          >
            <input
              type="checkbox"
              checked={Boolean(draft?.has(opt))}
              onChange={() => toggleOption(opt)}
              className="accent-[#236DB4] w-3.5 h-3.5"
            />
            <span className={`truncate ${opt === BLANK_FILTER_VALUE ? "italic text-slate-400" : ""}`}>
              {optionText(opt)}
            </span>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-[11px] font-medium text-[#236DB4] hover:underline"
            onClick={() => setDraft(new Set(options))}
          >
            {translations[langMode]?.all ?? "All"}
          </button>
          <button
            type="button"
            className="text-[11px] font-medium text-slate-400 hover:underline"
            onClick={() => setDraft(new Set())}
          >
            {translations[langMode]?.none ?? "None"}
          </button>
        </div>
        <button
          type="button"
          onClick={apply}
          disabled={loading || !draft}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96] disabled:opacity-50"
        >
          OK
        </button>
      </div>
    </FilterPopoverShell>
  );
};

export const Security = () => {
  const {
    theme,
    setParticularAccount,
    setIsEditRequest,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    selectedOrgId,
    langMode, projectId
  } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const [totalValue, setTotalValue] = useState(0);
  const [accountNo, setAccountNo] = useState("");
  const [searchScope, setSearchScope] = useState("all");
  const [list, setList] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [rowDeleteId, setRowDeleteId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const securityPerm = getPermissionByScreenCode("ACCOUNT_PROTECTION");
  const canCreateProtectedAccount = securityPerm?.canCreate;
  const canUpdateProtectedAccount = securityPerm?.canUpdate;
  const canDeleteProtectedAccount = securityPerm?.canDelete;

  // Column filters run on the server (/search); popup values come from /column-values.
  const [columnFilters, setColumnFilters] = useState({});
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  // True for ~900ms after the funnels are revealed so they pulse, as in TaskListView.
  const [justRevealedFilters, setJustRevealedFilters] = useState(false);
  const prevShowColumnFiltersRef = useRef(showColumnFilters);
  useEffect(() => {
    const wasShown = prevShowColumnFiltersRef.current;
    prevShowColumnFiltersRef.current = showColumnFilters;
    if (!showColumnFilters || wasShown) return;
    setJustRevealedFilters(true);
    const t = setTimeout(() => setJustRevealedFilters(false), 900);
    return () => clearTimeout(t);
  }, [showColumnFilters]);
  const [openFilterPopoverColId, setOpenFilterPopoverColId] = useState(null);
  const [popoverSearch, setPopoverSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState({ colId: null, items: [], loading: false });
  const columnValuesCacheRef = useRef(new Map());
  const filterButtonRefs = useRef({});

  const yesLabel = translations[langMode]?.yes ?? "Yes";
  const noLabel = translations[langMode]?.no ?? "No";
  const COLUMN_DEFS = [
    { id: "createdBy", label: translations[langMode].createdBy, filterable: true, filterType: "values" },
    { id: "accno", label: translations[langMode].accno, filterable: true, filterType: "values" },
    {
      id: "permanent",
      label: translations[langMode].permanent,
      filterable: true,
      filterType: "values",
      optionLabel: (value) => (value === "Yes" ? yesLabel : value === "No" ? noLabel : value),
    },
    { id: "fromdate", label: translations[langMode].fromdate, filterable: true, filterType: "date" },
    { id: "protectionValid", label: translations[langMode].protectionValid, filterable: true, filterType: "date" },
    { id: "cretDate", label: translations[langMode].cretDate, filterable: true, filterType: "date" },
    { id: "remark", label: translations[langMode].remark, filterable: true, filterType: "text" },
    { id: "previousRemarks", label: translations[langMode].previousRemarks, filterable: true, filterType: "text" },
    { id: "updatedBy", label: translations[langMode].updatedBy, filterable: true, filterType: "values" },
    { id: "updatedAt", label: translations[langMode].updatedat, filterable: true, filterType: "date" },
  ];

  const loadRequestIdRef = useRef(0);
  const loadAbortRef = useRef(null);

  const hasColumnFilters = Object.keys(columnFilters).length > 0;
  const activeFilterCount = Object.keys(columnFilters).length;
  const isFilteredView = hasColumnFilters || Boolean(accountNo);
  const searchFilters = toSearchFilters(columnFilters);
  const filtersKey = JSON.stringify(searchFilters);
  const isScopedSearch = Boolean(accountNo) && searchScope !== "all";

  const loadProtectedAccounts = async (pageOverride) => {
    const page = pageOverride ?? currentPage;
    // Overlapping loads (search-as-you-type, Reset) can resolve out of order; only the newest may update the list.
    const requestId = ++loadRequestIdRef.current;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    try {
      setLoading(true);
      const protectedAccounts = await searchProtectedAccounts(
        {
          projectId,
          page,
          size: pageSize,
          search: isScopedSearch ? "" : accountNo || "",
          columnFilters: isScopedSearch
            ? [...searchFilters, { column: searchScope, op: "contains", value: accountNo }]
            : searchFilters,
        },
        controller.signal
      );
      if (requestId !== loadRequestIdRef.current) return;
      const rows = (protectedAccounts.accountProtectionList || []).filter(
        (item) => item && item.id != null
      );
      setList(rows);
      setTotalValue(protectedAccounts.totalCount ?? rows.length);
      setLoading(false);
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error("Error loading protected accounts:", error);
      setList([]);
      setTotalValue(0);
      setLoading(false);
    }
  };

  // After create/edit/delete the popup values may have changed too.
  const reloadProtectedAccounts = (pageOverride) => {
    columnValuesCacheRef.current.clear();
    return loadProtectedAccounts(pageOverride);
  };

  // A new search or filter starts from page 0; the page change then triggers the load.
  const queryKey = `${accountNo ? searchScope : ""}|${accountNo}|${filtersKey}`;
  const prevQueryKeyRef = useRef(queryKey);
  useEffect(() => {
    if (prevQueryKeyRef.current !== queryKey) {
      prevQueryKeyRef.current = queryKey;
      setSelectedRecords([]);
      if (currentPage !== 0) {
        setCurrentPage(0);
        return;
      }
    }
    loadProtectedAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, selectedOrgId, projectId, queryKey]);

  useEffect(() => {
    columnValuesCacheRef.current.clear();
  }, [projectId]);

  // Popup values for the open column, narrowed by the other columns' filters.
  useEffect(() => {
    const colId = openFilterPopoverColId;
    const apiColumn = colId && FILTER_API_COLUMNS[colId];
    const isValueColumn = COLUMN_DEFS.some((c) => c.id === colId && c.filterType === "values");
    if (!apiColumn || !isValueColumn) return undefined;
    const body = {
      projectId,
      column: apiColumn,
      // Permanent shows translated Yes/No, so its two values are searched locally.
      search: colId === "permanent" ? "" : popoverSearch,
      limit: COLUMN_VALUES_LIMIT,
      columnConditions: toColumnConditions(columnFilters, colId),
    };
    const cacheKey = JSON.stringify(body);
    const cached = columnValuesCacheRef.current.get(cacheKey);
    if (cached) {
      setFilterOptions({ colId, items: cached, loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setFilterOptions((prev) => ({ colId, items: prev.colId === colId ? prev.items : [], loading: true }));
    fetchProtectionColumnValues(body, controller.signal)
      .then((values) => {
        const items = Array.from(new Set(values.map((v) => (v == null ? BLANK_FILTER_VALUE : String(v)))));
        columnValuesCacheRef.current.set(cacheKey, items);
        setFilterOptions({ colId, items, loading: false });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error("Error loading filter values:", error);
        setFilterOptions({ colId, items: [], loading: false });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFilterPopoverColId, popoverSearch, filtersKey, projectId]);

  const closeFilterPopover = () => {
    setOpenFilterPopoverColId(null);
    setPopoverSearch("");
  };

  const totalItems = totalValue;
  const totalPages = Math.ceil(totalItems / pageSize) - 1;
  const effectiveLastPageIndex = Math.max(totalPages, 0);
  const startIndex = currentPage * pageSize;
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;
  const displayEnd = totalItems > 0 ? Math.min(startIndex + pageSize, totalItems) : 0;
  const isBusy = loading;

  const resetTableFilters = () => {
    setColumnFilters({});
    closeFilterPopover();
    setCurrentPage(0);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (selectedOrgId && projectId) {
      setCurrentPage(0);
      setPageSize(10);
      // loadProtectedAccounts(); // redundant duplicate: the effect above (deps
      // include selectedOrgId/projectId) already re-fires on this same change
      // with up-to-date pagination, so this call always fetched the exact same
      // data a second time. Left here, commented, in case that assumption ever
      // stops holding -- uncomment to restore the direct call.
    }
  }, [selectedOrgId, projectId]);

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleCheckboxChange = (uniqueId) => {
    setSelectedRecords((prevSelected) => {
      let newSelected = [...prevSelected];
      if (newSelected.includes(uniqueId)) {
        newSelected = newSelected.filter((id) => id !== uniqueId);
      } else {
        newSelected.push(uniqueId);
      }
      return newSelected;
    });
  };

  const displayedRows = list.filter((item) => item && item.id != null);
  const displayedIds = displayedRows.map((item) => item.id);
  const allDisplayedSelected =
    displayedIds.length > 0 &&
    displayedIds.every((id) => selectedRecords.includes(id));

  const handleMasterCheckboxChange = () => {
    if (allDisplayedSelected) {
      setSelectedRecords((prev) =>
        prev.filter((id) => !displayedIds.includes(id))
      );
    } else {
      setSelectedRecords((prev) => {
        const next = new Set(prev);
        displayedIds.forEach((id) => next.add(id));
        return [...next];
      });
    }
  };

  const handleEditPopup = (userId, position) => {
    setEditUserId(userId);
    setPopupPosition(position);
    setShowEdit(true);
  };

  const closeEditPopup = () => {
    setShowEdit(false);
  };

  const Popup = ({ onClose, onEdit, position, userId }) => {
    if (!position) {
      return null;
    }
    const adjustedPosition = {
      x: position.x - 80,
      y: position.y,
    };

    return (
      <div
        className="fixed bg-opacity-50 flex items-center justify-center top-2"
        style={{
          top: adjustedPosition.y,
          left: adjustedPosition.x < 0 ? 0 : adjustedPosition.x,
          width: "100px",
        }}
      >
        <div
          className={`bg-white rounded p-3 shadow-lg relative ${theme === "dark"
            ? "dark-mode border "
            : theme === "high-contrast"
              ? "high-contrast  "
              : ""
            }`}
          style={{ width: "80%" }}
        >
          <ModalCloseButton
            className="absolute -top-2 -right-2 shadow-md z-[100]"
            onClick={onClose}
            size={14}
          />
          <button
            className={`inline-flex h-10 min-h-[40px] items-center justify-center rounded-[10px] px-4 py-2 text-xs sm:text-sm text-black ${theme === "dark"
              ? "dark-mode  "
              : theme === "high-contrast"
                ? "high-contrast  "
                : ""
              }`}
            onClick={() => onEdit(userId)}
          >
            {translations[langMode].edit}
          </button>
        </div>
      </div>
    );
  };

  const editProtectedAccount = async (editUserId) => {
    const accessToken = Cookies.get("accessToken");
    try {
      const fetchedParticularAccount = await fetchParticularAccount(
        accessToken,
        editUserId
      );
      setParticularAccount(fetchedParticularAccount);
      setIsEditRequest(true);
      setEditModalOpen(true);
    } catch (error) { }
  };

  const deleteRow = async () => {
    try {
      const data = await deleteProtectedAccounts([rowDeleteId]);
      if (data.header && data.header.code === 600) {
        setList((prev) => prev.filter((item) => item.id !== rowDeleteId));
        setTotalValue((prev) => Math.max(0, prev - 1));
        setSelectedRecords((prev) => prev.filter((id) => id !== rowDeleteId));
        columnValuesCacheRef.current.clear();
        toast.success(data.body.value);
      } else {
        toast.error(data.body.value);
      }
    } catch (error) {
      console.error("Error deleting protected account:", error);
    }
    setRowDeleteId(null);
  };

  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true, // Use 12-hour format with AM/PM
    };

    const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  return (
    <div className="flex min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex-col overflow-y-auto sm:overflow-hidden bg-white">
      <div
        className={`box-border flex min-h-full sm:h-full sm:min-h-0 w-full flex-col sm:overflow-hidden bg-white ${theme === "dark"
          ? "dark-mode  "
          : theme === "high-contrast"
            ? "high-contrast  "
            : ""
          }`}
      >
        <SecurityToolbar
          setAccountNo={setAccountNo}
          accountNo={accountNo}
          hasSelectedRecords={selectedRecords.length > 0}
          selectedRecords={selectedRecords}
          setSelectedRecords={setSelectedRecords}
          setList={setList}
          loadProtectedAccounts={reloadProtectedAccounts}
          canCreateProtectedAccount={canCreateProtectedAccount}
          canDeleteProtectedAccount={canDeleteProtectedAccount}
          showColumnFilters={showColumnFilters}
          setShowColumnFilters={setShowColumnFilters}
          hasActiveFilterOrSort={hasColumnFilters}
          activeFilterOrSortCount={activeFilterCount}
          resetTableFilters={resetTableFilters}
          totalItems={totalItems}
          itemsLoading={isBusy}
          searchScope={searchScope}
          setSearchScope={setSearchScope}
        />
        <div className="mx-2 mb-2 mt-2 flex min-h-[22rem] sm:min-h-0 min-w-0 flex-none sm:flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3">
          <div
            className={`scrollbar-container relative z-20 overflow-visible flex flex-wrap items-center justify-between px-3 py-2.5 border-b border-slate-100 bg-white sm:px-5 ${theme === "dark" ? "border-gray-600" : ""}`}
            style={{ zIndex: 20 }}
          >
            <div className="flex w-full items-center justify-between gap-3 sm:mx-auto sm:mt-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="flex items-center gap-3 text-xs text-slate-500 sm:me-2 sm:gap-4 sm:whitespace-nowrap">
                  <span className="inline sm:hidden font-medium">{`${displayStart}-${displayEnd} / ${totalItems}`}</span>
                  <span className="hidden sm:inline">
                    {translations[langMode].showing} <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>{" "}
                    {translations[langMode].of} <span className="rtl-keep-ltr">{totalItems}</span>
                  </span>
                </p>
              </div>
              <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:ml-auto sm:gap-3">
                <div className="flex items-center gap-1.5">
                  <label htmlFor="pageSize" className="hidden max-w-[96px] leading-tight sm:block sm:max-w-none sm:whitespace-nowrap">{translations[langMode].rows}</label>
                  <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
                </div>
                <span className="pager-nav flex items-center gap-1">
                  <PagerArrowButton
                    direction="prev"
                    isRtl={isRtl}
                    onClick={goToPreviousPage}
                    disabled={currentPage === 0}
                    ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                  />
                  <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                    {currentPage + 1} / {effectiveLastPageIndex + 1}
                  </span>
                  <PagerArrowButton
                    direction="next"
                    isRtl={isRtl}
                    onClick={goToNextPage}
                    disabled={currentPage === effectiveLastPageIndex}
                    ariaLabel={translations[langMode]?.nextPage || "Next page"}
                  />
                </span>
              </div>
            </div>
          </div>
          <div className="px-3 py-2 sm:px-5 flex flex-wrap gap-2">
            <div className="flex w-full items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 dark:border-amber-900/50 dark:bg-amber-950/30 sm:w-auto sm:max-w-max">
              <Info className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
              <p className="text-xs leading-5 text-amber-800 dark:text-amber-300 whitespace-normal">{translations[langMode].permanentProtection}</p>
            </div>
          </div>
          <TableScrollShell
            theme={theme}
            className="w-full flex-1 min-h-0"
            innerClassName="task-list-scrollbar"
          >
              <table
                className={`w-full min-w-[1150px] text-xs text-center text-gray-500 dark:text-gray-400 relative z-0 border-collapse ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
                dir={dir}
              >
                <thead
                  className="text-xs text-slate-700 border-b border-slate-200 dark:text-gray-400 sticky top-0 bg-slate-50 z-10"
                >
                  <tr className={theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}>
                    {canDeleteProtectedAccount && (
                      <th className="w-[40px] min-w-[40px] max-w-[40px] text-xs font-semibold uppercase py-3.5 px-0 text-gray-800 text-center">
                        <input
                          type="checkbox"
                          checked={allDisplayedSelected}
                          onChange={handleMasterCheckboxChange}
                          className="w-4 h-4 rounded border-slate-300 accent-[#236DB4] text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={translations[langMode]?.selectAll ?? "Select all on page"}
                        />
                      </th>
                    )}
                    <th className="w-[44px] min-w-[44px] max-w-[44px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 tracking-[0.04em] text-center">
                      {translations[langMode].number}
                    </th>
                    {(canUpdateProtectedAccount || canDeleteProtectedAccount) && (
                      <th className="w-[96px] min-w-[96px] max-w-[96px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-500 tracking-[0.04em] text-center">
                        {translations[langMode].Action}
                      </th>
                    )}
                    {COLUMN_DEFS.map((col) => {
                      return (
                        <th
                          key={col.id}
                          className="relative px-3 sm:px-4 py-2.5 sm:py-3.5 text-xs font-semibold uppercase text-slate-500 tracking-[0.04em] text-center whitespace-nowrap"
                        >
                          <span className="flex items-center justify-center gap-1 min-w-0">
                            <span className="min-w-0 whitespace-nowrap">{col.label}</span>
                            {col.filterable && showColumnFilters && (
                              <span className={justRevealedFilters ? "inline-flex shrink-0 animate-filter-pulse" : "inline-flex shrink-0"}>
                                <Tooltip text={`${translations[langMode]?.filterByColumn ?? "Filter by"} ${col.label}`} className="inline-flex">
                                  <button
                                    type="button"
                                    ref={(el) => { filterButtonRefs.current[col.id] = el; }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPopoverSearch("");
                                      setOpenFilterPopoverColId((prev) => (prev === col.id ? null : col.id));
                                    }}
                                    className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors text-[#236DB4] ${columnFilters[col.id] ? "bg-blue-50" : "hover:bg-blue-50"}`}
                                    aria-label={`${translations[langMode].filters} ${col.label}`}
                                  >
                                    <Filter size={12} fill={columnFilters[col.id] ? "currentColor" : "none"} />
                                  </button>
                                </Tooltip>
                              </span>
                            )}
                          </span>
                          {col.filterable && showColumnFilters && openFilterPopoverColId === col.id && col.filterType !== "values" && (() => {
                            const TypedFilter = col.filterType === "date" ? DateRangeColumnFilter : ContainsColumnFilter;
                            return (
                              <TypedFilter
                                value={columnFilters[col.id] ?? null}
                                onChange={(next) =>
                                  setColumnFilters((prev) => {
                                    const copy = { ...prev };
                                    if (next === null) delete copy[col.id];
                                    else copy[col.id] = next;
                                    return copy;
                                  })
                                }
                                onClose={closeFilterPopover}
                                anchorEl={filterButtonRefs.current[col.id]}
                                langMode={langMode}
                              />
                            );
                          })()}
                          {col.filterable && showColumnFilters && openFilterPopoverColId === col.id && col.filterType === "values" && (
                            <ColumnFilterPopover
                              options={filterOptions.colId === col.id ? filterOptions.items : []}
                              selected={columnFilters[col.id] ?? null}
                              onChange={(next) =>
                                setColumnFilters((prev) => {
                                  const copy = { ...prev };
                                  if (next === null) delete copy[col.id];
                                  else copy[col.id] = next;
                                  return copy;
                                })
                              }
                              onClose={closeFilterPopover}
                              anchorEl={filterButtonRefs.current[col.id]}
                              langMode={langMode}
                              loading={filterOptions.colId !== col.id || filterOptions.loading}
                              onSearchChange={setPopoverSearch}
                              getOptionLabel={col.optionLabel}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {!isBusy &&
                    displayedRows.map((item, index) => (
                      <tr
                        key={item.id ?? index}
                        className={`text-xs border-b border-slate-100 transition-colors duration-150 ${theme === "dark"
                          ? "dark-mode"
                          : theme === "high-contrast"
                            ? "high-contrast"
                            : "bg-white hover:bg-slate-50/70"
                          }`}
                      >

                        {canDeleteProtectedAccount && (
                          <td className="w-[40px] min-w-[40px] max-w-[40px] py-2.5 text-center text-[#4B5563]">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(item.id)}
                              onChange={() => handleCheckboxChange(item.id)}
                              className={`w-4 h-4 text-blue-800 bg-white border-blue-600 rounded-[3px] focus:ring-blue-800 dark:focus:ring-blue-800 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:border-gray-600 ${theme === "dark"
                                ? "dark-mode"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : ""
                                }`}
                            />
                          </td>
                        )}
                        <td className="w-[44px] min-w-[44px] max-w-[44px] px-0 py-2.5 text-xs text-[#70788f] whitespace-nowrap font-mono text-center">
                          {startIndex + index + 1}
                        </td>
                        {(canUpdateProtectedAccount || canDeleteProtectedAccount) && (
                          <td className="w-[96px] min-w-[96px] max-w-[96px] py-2.5 text-center">
                            <div className="flex items-center justify-center gap-0.5">
                              {canUpdateProtectedAccount && (
                                <ActionIconButton
                                  onClick={() => editProtectedAccount(item.id)}
                                  tone="blue"
                                  ariaLabel={translations[langMode].edit}
                                  tooltipText={translations[langMode]?.editProtectedAccountTooltip ?? "Edit protected account"}
                                  tooltipPosition="top"
                                  className="!h-7 !w-7 !rounded-md"
                                >
                                  <Pencil size={14} />
                                </ActionIconButton>
                              )}
                              {canDeleteProtectedAccount && (
                                <ActionIconButton
                                  onClick={() => setRowDeleteId(item.id)}
                                  tone="red"
                                  ariaLabel={translations[langMode].delete}
                                  tooltipText={translations[langMode]?.deleteProtectedAccountTooltip ?? "Delete protected account"}
                                  tooltipPosition="top"
                                  className="!h-7 !w-7 !rounded-md"
                                >
                                  <Trash2 size={14} />
                                </ActionIconButton>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:overflow-hidden" dir="auto">
                          <TruncatedCellText text={item.createdByName || "-"} langMode={langMode} />
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:overflow-hidden" dir="auto">
                          <TruncatedCellText text={item.accountNo} langMode={langMode} />
                        </td>
                        <td className="px-2 py-2.5 text-xs truncate overflow-hidden whitespace-nowrap text-center" dir="auto">
                          {item.permanent ? (
                            <span className="text-[11px] px-2 py-1 font-semibold rounded-md border border-current/15 uppercase tracking-[0.10em] inline-flex items-center gap-1.5 text-[#29a36a] bg-[#e8f7f0]">
                              {translations[langMode]?.yes ?? "Yes"}
                            </span>
                          ) : (
                            <span className="text-[11px] px-2 py-1 font-semibold rounded-md border border-current/15 uppercase tracking-[0.10em] inline-flex items-center gap-1.5 text-[#838995] bg-[#eeeff1]">
                              {translations[langMode]?.no ?? "No"}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:truncate lg:overflow-hidden">
                          {formattedDate(item.fromDate)}
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:truncate lg:overflow-hidden">
                          {formattedDate(item.toDate)}
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:truncate lg:overflow-hidden">
                          {formattedDate(item.createdAt)}
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center max-w-[200px] lg:max-w-[260px]" dir="auto">
                          <TruncatedCellText text={item.remarks} langMode={langMode} className="min-w-0" />
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center max-w-[200px] lg:max-w-[260px]" dir="auto">
                          <TruncatedCellText text={item.previousRemarks || "-"} langMode={langMode} className="min-w-0" />
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:overflow-hidden" dir="auto">
                          <TruncatedCellText text={item.updatedByName || "-"} langMode={langMode} />
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal whitespace-nowrap text-center lg:truncate lg:overflow-hidden">
                          {formattedDate(item.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  {!isBusy && displayedRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={(canDeleteProtectedAccount ? 2 : 1) + COLUMN_DEFS.length + ((canUpdateProtectedAccount || canDeleteProtectedAccount) ? 1 : 0)}
                        className="py-16 h-[200px] align-middle"
                      >
                        {/* Same empty state as StockManagement. */}
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
                            {isFilteredView ? <SearchX size={22} /> : <Inbox size={22} />}
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {isFilteredView
                              ? (translations[langMode]?.noRecordsMatchSearch ?? "No records match your search")
                              : translations[langMode].noDatavb}
                          </div>
                          <div className="text-xs mt-1 max-w-xs text-slate-400">
                            {isFilteredView
                              ? (translations[langMode]?.tryAdjustingFilters ?? "Try adjusting your filters or search terms to find what you're looking for.")
                              : (translations[langMode]?.protectedAccountsEmptyHint ?? "No accounts are protected yet. Create one to protect it from automatic write-offs and closures.")}
                          </div>
                          {hasColumnFilters && (
                            <button data-button-intent="warning"
                              type="button"
                              onClick={resetTableFilters}
                              className="mt-3 h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                            >
                              {translations[langMode]?.clearFilters ?? "Clear filters"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {isBusy && <Spinner />}
          </TableScrollShell>
        </div>
      </div>
      {showEdit && (
        <Popup
          onClose={closeEditPopup}
          onEdit={() => editProtectedAccount(editUserId)}
          position={popupPosition}
          userId={editUserId}
        />
      )}
      {rowDeleteId && (
        <DeletePermission
          deleteFunction={deleteRow}
          onClose={() => setRowDeleteId(null)}
          onDeleteText={translations[langMode].aresurewanttodeleterecord}
          confirmText={translations[langMode].delete}
          confirmVariant="danger"
        />
      )}
      <CreateAccountModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSaved={reloadProtectedAccounts}
      />
    </div>
  );
};

